import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Copy, RefreshCw, Send } from "lucide-react";
import { useHd } from "../HelpdeskContext";
import { apiBase, hd, errorMessage } from "../api";
import { useSettings } from "./GeneralSettings";
import { SaveBar, Section, Toggle } from "./common";
import { T, TYPE } from "../../user/complaints/shared";

// Two-way WhatsApp through the workspace's WABA API. The access token is
// write-only: it is sent once, stored encrypted, and only its last 4
// characters come back.
export default function WhatsAppSettings() {
  const ctx = useHd();
  const { s, set, save, saving, dirty, reset } = useSettings();
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [webhookPath, setWebhookPath] = useState(null);
  if (!s) return <CircularProgress size={22} />;
  const wa = s.whatsapp || {};
  const setW = (k) => set("whatsapp", k);
  const path = webhookPath || wa.webhookPath;
  const webhookUrl = path ? `${apiBase()}${path}` : "";
  const connected = wa.baseUrl && wa.hasToken;

  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      await hd.testWhatsApp({ phone: testPhone });
      setResult({ ok: true, text: `Test message sent to ${testPhone}` });
    } catch (err) {
      setResult({ ok: false, text: errorMessage(err, "The WhatsApp API did not accept the message") });
    } finally {
      setTesting(false);
    }
  };
  const rotate = async () => {
    if (!window.confirm("Create a new webhook URL? The old one stops working, so update it in your WhatsApp provider.")) return;
    try {
      const r = await hd.rotateWhatsAppWebhook();
      setWebhookPath(r.webhookPath);
      ctx.notify("New webhook URL created");
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };

  return (
    <Box>
      <Section title="Connect WhatsApp" description="Customers message your WhatsApp Business number; each conversation becomes a ticket and agents reply from the helpdesk.">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="WhatsApp API URL" placeholder="https://omni.justtawk.com/api/<workspace-id>" value={wa.baseUrl || ""} onChange={(e) => setW("baseUrl")(e.target.value)}
              helperText="The base URL of your WABA API (JustTawk omni or another provider). Must start with https://" />
          </Grid>
          <Grid item xs={12}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
              <TextField fullWidth size="small" type="password" autoComplete="new-password"
                label={wa.hasToken ? `Access token (saved, ends in ${wa.keyLast4 || "····"}) — type to replace` : "Access token"}
                value={wa.token || ""} onChange={(e) => setW("token")(e.target.value)}
                helperText="Sent as “Authorization: Bearer <token>”. Stored encrypted; it is never shown again." />
              {wa.hasToken && <Button size="small" color="error" sx={{ whiteSpace: "nowrap", mt: { sm: 0.5 } }} onClick={() => { setW("token")(null); setW("enabled")(false); }}>Remove token</Button>}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Business WhatsApp number" placeholder="+91 98xxxxxxx" value={wa.phoneNumber || ""} onChange={(e) => setW("phoneNumber")(e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="New WhatsApp tickets go to" value={wa.defaultTeamId || ""} onChange={(e) => setW("defaultTeamId")(e.target.value || null)}>
              <MenuItem value="">Normal assignment rules</MenuItem>
              {ctx.teams.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Send text path" value={wa.textPath || ""} onChange={(e) => setW("textPath")(e.target.value)} helperText="POST { phone_number, message }" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Send template path" value={wa.templatePath || ""} onChange={(e) => setW("templatePath")(e.target.value)} helperText="For approved templates outside the 24-hour window" /></Grid>
        </Grid>
        <Box sx={{ mt: 1.5 }}>
          <Toggle label="Turn on two-way WhatsApp chat" checked={Boolean(wa.enabled)} onChange={setW("enabled")}
            help={!connected ? "Save the API URL and access token first." : "Incoming messages create tickets; agent replies on WhatsApp tickets go out on WhatsApp."} />
        </Box>
      </Section>

      <Section title="Incoming messages (webhook)" description="Give this URL to your WhatsApp provider as the webhook for incoming messages. For Meta-style verification, use the last part of the URL as the verify token.">
        {webhookUrl ? (
          <>
            <Box sx={{ p: 1.25, bgcolor: "#101828", color: "#E4E7EC", borderRadius: "8px", fontFamily: "monospace", fontSize: "0.78rem", wordBreak: "break-all" }}>{webhookUrl}</Box>
            <Button size="small" startIcon={<Copy size={14} />} sx={{ mt: 1 }} onClick={() => { navigator.clipboard?.writeText(webhookUrl); ctx.notify("Webhook URL copied"); }}>Copy URL</Button>
            <Button size="small" startIcon={<RefreshCw size={14} />} sx={{ mt: 1, ml: 1 }} onClick={rotate}>New URL</Button>
          </>
        ) : (
          <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>The webhook URL appears after you turn WhatsApp on and save.</Typography>
        )}
      </Section>

      <Section title="Send a test message">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <TextField size="small" label="Phone number" placeholder="919876543210" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} sx={{ minWidth: 220 }} />
          <Button size="small" variant="outlined" startIcon={testing ? <CircularProgress size={14} /> : <Send size={14} />} disabled={testing || dirty || !connected || !testPhone.trim()} onClick={test}>Send test</Button>
          {dirty && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Save first, then test.</Typography>}
        </Stack>
        {result && <Alert severity={result.ok ? "success" : "error"} sx={{ mt: 1.5 }}>{result.text}</Alert>}
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1 }}>WhatsApp only delivers free-text messages within 24 hours of the customer's last message; outside that window a template is needed.</Typography>
      </Section>

      <Section title="Automation-only gateway (legacy)" description="Optional. Used by the “Send WhatsApp” automation action when no API URL is set above: POST { to, message } to this URL with the same token.">
        <TextField fullWidth size="small" label="Provider URL" value={wa.apiUrl || ""} onChange={(e) => setW("apiUrl")(e.target.value)} />
      </Section>
      <SaveBar dirty={dirty} saving={saving} onSave={() => save(["whatsapp"])} onReset={reset} />
    </Box>
  );
}
